import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import FileIcon from '~/assets/svg/newSvg/file.svg?react';
import cn from 'classnames';
import styles from './StorageFolderFiles.module.scss';
import { t } from 'i18next';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import DeleteSvg from '~/assets/svg/newSvg/delete.svg?react';
import { IconButton } from '~/ui';
import { useToastSuccess } from '~/utils/useToastSuccess';

interface IStorageFiles {
  id: string;
  refetchAssets: () => void;
}

interface IData {
  id: number;
  name: string;
  path: string;
  type: string;
  full_path: string;
}

const StorageFolderFiles = ({ id, refetchAssets }: IStorageFiles) => {
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();

  const { mutateAsync: deleteFile } = useMutation(
    async (fileId: number) => {
      const res = await instance.delete(`admin/documents/${fileId}`);
      return res;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['documents']);
        toastSuccess(t('file_successfully_deleted'));
        refetch();
        refetchAssets();
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data, isLoading, refetch } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/documents', {
        params: { asset_id: id },
      });
      return data;
    },
    queryKey: ['documents', id],
    onError: error => {
      toastError(error);
    },
  });

  const isImageFile = [
    'webp',
    'png',
    'jpg',
    'jpeg',
    'ico',
    'icon',
    'bmp',
    'gif',
    'svg',
    'tiff',
    'avif',
    'jfif',
  ];

  return (
    <div className={styles.filesWrapper}>
      {data && !!data.data.length && !isLoading && (
        <ul className={styles.filesList}>
          {data?.data.map((item: IData) => (
            <li key={item.id} className={styles.fileItem}>
              <div
                className={cn(styles.imageWrapper, {
                  [styles.purpleBg]: !isImageFile.includes(
                    item.type.toLowerCase()
                  ),
                })}
              >
                {!isImageFile.includes(item.type.toLowerCase()) ? (
                  <FileIcon />
                ) : (
                  <img src={item.full_path} className={styles.img} />
                )}
                <ModalConfirm
                  title={t('sure_delete_file')}
                  onConfirm={() => deleteFile(item.id)}
                >
                  {open => (
                    <IconButton onClick={open} className={styles.deleteBtn}>
                      <DeleteSvg className={styles.deleteSvg} />
                    </IconButton>
                  )}
                </ModalConfirm>
              </div>
              <p className={styles.fileName}>{`${item.name}`}</p>
            </li>
          ))}
        </ul>
      )}
      {data && !isLoading && data?.data?.length === 0 && (
        <p className={styles.empty}>{t('empty')}</p>
      )}
    </div>
  );
};

export default StorageFolderFiles;
